import {styled, Slider, Box, Typography} from '@mui/material'

/**
 * StyledSlider - Custom styled MUI Slider with gradient or solid color support
 */
export const StyledSlider = styled(Slider, {
  shouldForwardProp: (prop) => prop !== 'useGradient'
})(({theme, useGradient, value, min = 0, max = 100}) => {
  const solidColor = theme.palette.hpPrimary
  // Define gradient colors from reference (0%, 25%, 52%, 75%, 100%)
  const gradientColors = {
    0: theme.palette.hpPrimaryLightest,
    25: theme.palette.hpPrimaryLight,
    52: theme.palette.hpPrimary,
    75: theme.palette.hpPrimaryDark,
    100: theme.palette.hpPrimaryDarker
  }

  // Calculate the gradient portion based on actual values
  let trackColor = solidColor
  if (useGradient && Array.isArray(value)) {
    const [minVal] = value

    // Calculate what percentage of the slider's range the minVal represents
    // This converts the actual value to a position percentage on the slider (0-100%)
    const sliderRange = max - min
    const percentileStart = ((minVal - min) / sliderRange) * 100

    // Shift gradient stops so the track shows the correct percentile range
    trackColor = `linear-gradient(90deg, 
      ${gradientColors[0]} ${-percentileStart}%, 
      ${gradientColors[25]} ${25 - percentileStart}%, 
      ${gradientColors[52]} ${52 - percentileStart}%, 
      ${gradientColors[75]} ${75 - percentileStart}%, 
      ${gradientColors[100]} ${100 - percentileStart}%
    )`
  }

  return {
    height: 4,
    padding: '15px 0',
    '& .MuiSlider-track': {
      border: 'none',
      background: trackColor,
      height: 4
    },
    '& .MuiSlider-rail': {
      opacity: 0.3,
      backgroundColor: theme.palette.hpBlack,
      height: 4
    },
    '& .MuiSlider-thumb': {
      height: 20,
      width: 20,
      backgroundColor: theme.palette.hpWhite,
      border: `1px solid ${solidColor}`,
      boxShadow: `0 2px 4px ${theme.palette.hpAccentBlack}33`,
      '&:focus, &:hover, &.Mui-active': {
        boxShadow: useGradient ? `0 0 0 8px ${theme.palette.hpPrimary}29` : `0 0 0 8px ${solidColor}26`,
        // Reset on touch devices
        '@media (hover: none)': {
          boxShadow: `0 2px 4px ${theme.palette.hpAccentBlack}33`
        }
      },
      '&::before': {
        boxShadow: 'none'
      },
      '&::after': {
        width: 8,
        height: 8
      }
    }
  }
})

/**
 * SliderContainer - Container for the slider with proper spacing
 */
export const SliderContainer = styled(Box)(({theme}) => ({
  width: '100%',
  padding: '0 8px'
}))

/**
 * InputContainer - Container for input boxes
 */
export const InputContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'normal',
  marginTop: theme.spacing(0),
  gap: theme.spacing(1),
  '& .MuiTextField-root': {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.grey[300]
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main
      },
      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main
      }
    },
    '& input': {
      padding: '8px 12px',
      fontSize: '14px',
      fontWeight: 500
    }
  }
}))

/**
 * ErrorText - Styled error message text
 */
export const ErrorText = styled(Typography)(({theme}) => ({
  color: theme.palette.error.main,
  fontSize: '12px',
  marginTop: theme.spacing(1),
  textAlign: 'center',
  padding: theme.spacing(0.5),
  backgroundColor: theme.palette.error.light + '10',
  borderRadius: '4px',
  fontWeight: 500
}))

/**
 * CustomLabelsContainer - Container for custom labels below the slider
 */
export const CustomLabelsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start'
}))

/**
 * CustomLabel - Individual label component
 */
export const CustomLabel = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.5)
}))

/**
 * LabelText - The descriptive text label
 */
export const LabelText = styled(Typography)(({theme}) => ({
  fontSize: '12px',
  fontWeight: 400,
  color: theme.palette.hpBaseDarker,
  lineHeight: 1.2
}))
